import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [googleStatus, setGoogleStatus] = useState('loading');
  const googleBtnRef = useRef(null);
  const googleRenderedRef = useRef(false);
  const navigate = useNavigate();
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '765799705459-kd3p7qee43a056lc9tdprmp223svtgkr.apps.googleusercontent.com';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, { email, password });
      if (response.data?.token) {
        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('userProfile', JSON.stringify(response.data.user));
        window.dispatchEvent(new Event('auth-changed'));
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (credential) => {
    setError('');
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/google`, {
        idToken: credential,
      });
      if (response.data?.token) {
        localStorage.setItem('userToken', response.data.token);
        localStorage.setItem('userProfile', JSON.stringify(response.data.user));
        window.dispatchEvent(new Event('auth-changed'));
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Google sign-in failed');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let intervalId;
    let timeoutId;
    const initGoogleButton = () => {
      if (!window.google || !googleBtnRef.current) return false;
      if (googleRenderedRef.current) return true;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: (response) => {
          if (response?.credential) {
            handleGoogleCredential(response.credential);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        width: Math.min(320, Math.max(220, window.innerWidth - 72)),
      });
      googleRenderedRef.current = true;
      setGoogleStatus('ready');
      return true;
    };

    if (!initGoogleButton()) {
      intervalId = setInterval(() => {
        if (initGoogleButton()) {
          clearInterval(intervalId);
        }
      }, 300);
      timeoutId = setTimeout(() => {
        if (!googleRenderedRef.current) {
          setGoogleStatus('unavailable');
          if (intervalId) clearInterval(intervalId);
        }
      }, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [googleClientId, handleGoogleCredential]);

  return (
    <div className="auth-page page-shell">
      <div className="auth-card section-card">
        <span className="tag">Welcome back</span>
        <h1 className="page-title">Sign in to your account</h1>
        <p className="page-lead">
          Manage licenses, downloads, support conversations, and product updates in one place.
        </p>

        <div className={`google-signin-shell ${googleStatus}`}>
          <div ref={googleBtnRef} className="google-button-slot"></div>
          {googleStatus === 'loading' && <p>Loading Google sign-in...</p>}
          {googleStatus === 'unavailable' && (
            <p>Google sign-in could not load.</p>
          )}
        </div>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="form-grid">
          <div className="form-group">
            <label htmlFor="login-email">Email</label>
            <input
              id="login-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@domain.com"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="login-password">Password</label>
            <input
              id="login-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="••••••••"
              required
            />
          </div>
          <div className="auth-inline">
            <Link to="/forgot-password" className="link-muted">Forgot password?</Link>
          </div>
          {error && <p className="auth-error">{error}</p>}
          <button type="submit" disabled={isLoading}>{isLoading ? 'Signing in...' : 'Sign In'}</button>
        </form>

        <p className="auth-footer">
          New here? <Link to="/signup">Create an account</Link>
        </p>
      </div>
    </div>
  );
}

export default Login;
